import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  MenuItem,
  Switch,
  FormControlLabel,
  RadioGroup,
  Radio,
  FormLabel,
  FormControl,
  LinearProgress,
  Alert,
  Paper,
  IconButton,
  Chip,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  InsertDriveFile as FileIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { takePendingFile } from '../../../platform/search/pendingUpload.js';
import { uploadDocument } from '../../../services/documentService';
import { triggerCaseStudyIngest } from '../../../services/n8nService';

/** Allowed MIME types matching storage.rules */
/** Document types (P5-13) stored on Hugo_Documents.category. */
export const DOCUMENT_TYPES = [
  { value: 'incident', label: 'Incident report' },
  { value: 'investigation', label: 'Investigation report' },
  { value: 'audit', label: 'Audit report' },
  { value: 'observation', label: 'Observation report' },
  { value: 'regulatory', label: 'Regulatory report' },
  { value: 'causal_analysis', label: 'Causal analysis' },
];

const ALLOWED_TYPES = {
  'application/pdf': 'PDF',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
  'text/plain': 'TXT',
  'text/csv': 'CSV',
};

/** Also match by file extension as a fallback (some browsers don't set MIME correctly) */
const ALLOWED_EXTENSIONS = ['.pdf', '.docx', '.txt', '.csv'];

const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25 MB

/**
 * Validate that a file is an allowed type and under the size limit.
 * @param {File} file
 * @returns {{ valid: boolean, error?: string }}
 */
function validateFile(file) {
  // Check file type by MIME or extension
  const ext = '.' + file.name.split('.').pop().toLowerCase();
  const typeAllowed = ALLOWED_TYPES[file.type] || ALLOWED_EXTENSIONS.includes(ext);
  if (!typeAllowed) {
    return {
      valid: false,
      error: `File type not allowed. Accepted formats: PDF, DOCX, TXT, CSV.`,
    };
  }

  if (file.size > MAX_FILE_SIZE) {
    const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
    return {
      valid: false,
      error: `File too large (${sizeMB} MB). Maximum size is 25 MB.`,
    };
  }

  return { valid: true };
}

/**
 * Format file size for display.
 * @param {number} bytes
 * @returns {string}
 */
function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * DocumentUpload — Drag-and-drop file upload component with validation and progress.
 *
 * @param {Object} props
 * @param {string} props.orgId - Organization ID for the upload
 * @param {string} props.userId - Current user's UID
 * @param {Function} [props.onUploadComplete] - Called with { docId, downloadURL } on success
 * @param {Function} [props.onError] - Called with error on failure
 */
export default function DocumentUpload({ orgId, userId, onUploadComplete, onError }) {
  const [file, setFile] = useState(null);
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState(''); // P5-13 document type
  const [shareGlobal, setShareGlobal] = useState(false); // P5-15 "Add to REPSit Common Database" — default OFF = org-only
  // Tim Rev 2 (T32): where the document comes from. Industry reports default the Common Database toggle ON;
  // our organization's own documents default it OFF. The toggle can still be flipped either way.
  const [documentSource, setDocumentSource] = useState('organization'); // 'industry' | 'organization'
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const fileInputRef = useRef(null);

  const resetForm = useCallback(() => {
    setFile(null);
    setDescription('');
    setCategory('');
    setShareGlobal(false);
    setProgress(0);
    setError(null);
    setUploading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handleFileSelect = useCallback((selectedFile) => {
    setError(null);
    setSuccess(null);

    if (!selectedFile) return;

    const validation = validateFile(selectedFile);
    if (!validation.valid) {
      setError(validation.error);
      return;
    }

    setFile(selectedFile);
  }, []);

  // P6-01: a file attached on the entry page ("+") lands here ready to upload
  useEffect(() => {
    const pending = takePendingFile();
    if (pending) handleFileSelect(pending);
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOver(false);

      const droppedFile = e.dataTransfer?.files?.[0];
      if (droppedFile) {
        handleFileSelect(droppedFile);
      }
    },
    [handleFileSelect]
  );

  const handleInputChange = useCallback(
    (e) => {
      const selected = e.target.files?.[0];
      if (selected) {
        handleFileSelect(selected);
      }
    },
    [handleFileSelect]
  );

  const handleBrowseClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleRemoveFile = useCallback(() => {
    setFile(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handleUpload = useCallback(async () => {
    if (!file || !orgId) return;

    setUploading(true);
    setProgress(0);
    setError(null);
    setSuccess(null);

    try {
      const result = await uploadDocument(
        orgId,
        file,
        {
          title: file.name,
          description: description.trim(),
          category,
          documentSource,
          targetVisibility: shareGlobal ? 'global' : 'org_only',
          uploadedBy: userId || '',
        },
        (pct) => setProgress(pct)
      );

      setSuccess(`"${file.name}" uploaded successfully. AI assistant is analyzing it now.`);
      // Fire-and-forget: kick off n8n Workflow 1 (Case Study Ingest)
      triggerCaseStudyIngest(result.docId, {
        downloadURL: result.downloadURL,
        fileName: file.name,
        contentType: file.type,
      }, { organizationId: orgId, category, documentSource, targetVisibility: shareGlobal ? 'global' : 'org_only' });
      resetForm();

      if (typeof onUploadComplete === 'function') {
        onUploadComplete(result);
      }
    } catch (err) {
      const msg = err?.message || 'Upload failed. Please try again.';
      setError(msg);
      setUploading(false);
      setProgress(0);

      if (typeof onError === 'function') {
        onError(err);
      }
    }
  }, [file, orgId, userId, description, category, documentSource, shareGlobal, resetForm, onUploadComplete, onError]);

  // Determine the file type label for the chip
  const fileTypeLabel = file
    ? ALLOWED_TYPES[file.type] ||
      file.name.split('.').pop().toUpperCase()
    : '';

  return (
    <Box>
      {/* Success alert */}
      {success && (
        <Alert
          severity="success"
          icon={<CheckCircleIcon />}
          onClose={() => setSuccess(null)}
          sx={{ mb: 2 }}
        >
          {success}
        </Alert>
      )}

      {/* Error alert */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Drag-and-drop zone */}
      <Paper
        variant="outlined"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        sx={{
          p: 4,
          textAlign: 'center',
          cursor: 'pointer',
          borderStyle: 'dashed',
          borderWidth: 2,
          borderColor: dragOver
            ? 'primary.main'
            : file
            ? 'success.main'
            : 'divider',
          backgroundColor: dragOver
            ? 'action.hover'
            : file
            ? 'success.light'
            : 'background.default',
          transition: 'all 0.2s ease',
          '&:hover': {
            borderColor: 'primary.main',
            backgroundColor: 'action.hover',
          },
        }}
        onClick={!file ? handleBrowseClick : undefined}
      >
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.docx,.txt,.csv,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,text/csv"
          style={{ display: 'none' }}
          onChange={handleInputChange}
        />

        {file ? (
          /* File selected state */
          <Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                mb: 1,
              }}
            >
              <FileIcon color="success" sx={{ fontSize: 32 }} />
              <Typography variant="subtitle1" fontWeight="bold">
                {file.name}
              </Typography>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveFile();
                }}
                aria-label="Remove file"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
              <Chip label={fileTypeLabel} size="small" color="primary" variant="outlined" />
              <Chip label={formatFileSize(file.size)} size="small" variant="outlined" />
            </Box>
          </Box>
        ) : (
          /* Empty state */
          <Box>
            <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography variant="subtitle1" gutterBottom>
              Drag & drop a file here
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              or
            </Typography>
            <Button variant="outlined" size="small" onClick={handleBrowseClick}>
              Browse Files
            </Button>
            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
              Accepted: PDF, DOCX, TXT, CSV — Max 25 MB
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Document type (P5-13), description, Common Database toggle (P5-15) */}
      {file && (
        <Box sx={{ mt: 2 }}>
          <FormControl component="fieldset" disabled={uploading} sx={{ mb: 2 }}>
            <FormLabel component="legend" sx={{ fontSize: 13, fontWeight: 600 }}>Document source</FormLabel>
            <RadioGroup
              row
              value={documentSource}
              onChange={(e) => {
                const v = e.target.value;
                setDocumentSource(v);
                setShareGlobal(v === 'industry');
              }}
            >
              <FormControlLabel value="industry" control={<Radio size="small" />} label="Industry report (e.g., CSB, NERC, agency or public investigation)" />
              <FormControlLabel value="organization" control={<Radio size="small" />} label="Our organization's document" />
            </RadioGroup>
            <Typography variant="caption" color="text.secondary">
              {documentSource === 'industry'
                ? 'Industry reports are filed under Industry Case Studies and shared with the REPSit Common Database by default.'
                : "Your organization's documents are filed under Organizational Documents and stay private by default."}
            </Typography>
          </FormControl>
          <TextField
            select
            label="Document type"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            fullWidth
            size="small"
            disabled={uploading}
            helperText="Helps the assistant categorize the case study it builds from this document."
          >
            <MenuItem value="">Not sure / other</MenuItem>
            {DOCUMENT_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
          </TextField>
          <TextField
            label="Description (optional)"
            placeholder="Brief description of the document..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            maxRows={4}
            sx={{ mt: 2 }}
            disabled={uploading}
          />
          <FormControlLabel
            sx={{ mt: 1.5, alignItems: 'flex-start' }}
            control={<Switch checked={shareGlobal} onChange={(e) => setShareGlobal(e.target.checked)} disabled={uploading} />}
            label={
              <Box>
                <Typography variant="body2" fontWeight={600}>Add to REPSit Common Database</Typography>
                <Typography variant="caption" color="text.secondary">
                  {shareGlobal
                    ? 'ON — the case study built from this document joins the Industry Case Studies library for every REPSit organization (after REPSit.ai review).'
                    : 'OFF — the document and its case study stay private to your organization (Organizational Documents).'}
                </Typography>
              </Box>
            }
          />
        </Box>
      )}

      {/* Upload progress */}
      {uploading && (
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Uploading...
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {progress}%
            </Typography>
          </Box>
          <LinearProgress variant="determinate" value={progress} />
        </Box>
      )}

      {/* Upload button */}
      {file && !uploading && (
        <Button
          variant="contained"
          startIcon={<CloudUploadIcon />}
          onClick={handleUpload}
          disabled={!file || !orgId}
          sx={{ mt: 2 }}
          fullWidth
        >
          Upload Document
        </Button>
      )}
    </Box>
  );
}
